import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { refreshAbortController } from '@/apis/axios.utils';
import * as socialListeningApi from '@/apis/social-listening';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import {
  POST_VOLUME_GRAPH_OPTIONS,
  SOCIAL_LISTENING_CHANNEL_OPTIONS,
} from '@/app/socialListening/constants';
import {
  convertToScopedTopicFilters,
  getDateRangeByOffset,
} from '@/app/socialListening/utils/filter-utils';
import { useIdentityStore } from '@/stores/identity';
import { useSocialListeningStore } from '@/stores/social-listening';

export const useSocialListeningComparisonsStore = defineStore('socialListeningComparisons', () => {
  const { organizationId, getTimeSeriesPostVolume } = useSocialListening();
  const socialListeningStore = useSocialListeningStore();
  const identityStore = useIdentityStore();

  const abortControllers = {
    postsPerTopicPerChannel: ref(null),
    topicsPostVolumePerGender: ref(null),
    topicsPostVolumePerAge: ref(null),
    topicsStats: ref(null),
  };

  const pendingDataPull = ref({
    topicsPostVolumePerChannel: true,
    topicsPostVolumePerGender: true,
    topicsPostVolumePerAge: true,
    topicsStats: true,
  });

  const comparisonList = ref([]);
  async function fetchComparisonList() {
    try {
      const response = await socialListeningApi.getComparisons(organizationId.value);
      comparisonList.value = response.data.data;
    } catch {
      comparisonList.value = [];
    }
  }
  const comparisonsUsage = computed(() => {
    const orgPermissions = identityStore.identity.organization_permissions[organizationId.value];
    const topicLimit = orgPermissions.social_listening.social_listening_topic_limit;
    return {
      comparisonsLimit: topicLimit,
      comparisonsCreated: comparisonList.value.length,
    };
  });
  const hasExceededComparisonsLimit = computed(
    () => comparisonsUsage.value.comparisonsCreated >= comparisonsUsage.value.comparisonsLimit,
  );

  const draftMultiTopicComparison = ref([]);
  const draftMultiTopicComparisonIsValid = computed(() => {
    if (!draftMultiTopicComparison.value || draftMultiTopicComparison.value.length < 2) {
      return false;
    }

    return draftMultiTopicComparison.value.every((topic) => {
      if (!topic || typeof topic !== 'object') {
        return false;
      }

      if (!Array.isArray(topic.keywordGroups) || topic.keywordGroups.length < 1) {
        return false;
      }

      return topic.keywordGroups.every((group) => {
        if (!group || typeof group !== 'object') {
          return false;
        }

        if (typeof group.level !== 'number') {
          return false;
        }

        if (!Array.isArray(group.keywords) || group.keywords.length < 1) {
          return false;
        }
        if (!group.keywords.every((keyword) => typeof keyword === 'string')) {
          return false;
        }

        if (!Array.isArray(group.operators) || group.operators.length < 1) {
          return false;
        }
        return group.operators.every((operator) => typeof operator === 'string');
      });
    });
  });

  function addTopicToDraftComparison(topic, arrayIndex = undefined) {
    if (typeof arrayIndex === 'number' && arrayIndex >= 0) {
      draftMultiTopicComparison.value.splice(arrayIndex, 1, topic);
    } else {
      draftMultiTopicComparison.value.push(topic);
    }
  }
  function removeTopicFromDraftComparison({ topicId, arrayIndex }) {
    if (topicId) {
      draftMultiTopicComparison.value = draftMultiTopicComparison.value.filter(
        (topic) => topic.id !== topicId,
      );
    } else if (typeof arrayIndex === 'number' && arrayIndex >= 0) {
      draftMultiTopicComparison.value.splice(arrayIndex, 1);
    }
  }

  function renameTopicFromDraftComparison(arrayIndex, newTopicName) {
    if (typeof arrayIndex === 'number' && arrayIndex >= 0) {
      draftMultiTopicComparison.value[arrayIndex].topicName = newTopicName;
    }
  }

  function clearDraftComparison() {
    draftMultiTopicComparison.value = [];
  }

  function updateTopicKeywordGroups(topicIndex, keywordGroups) {
    if (
      draftMultiTopicComparison.value[topicIndex] &&
      typeof topicIndex === 'number' &&
      topicIndex >= 0
    ) {
      draftMultiTopicComparison.value[topicIndex].keywordGroups = keywordGroups;
    }
  }

  const selectedComparison = ref(null);
  const selectedComparisonTopics = computed(() => selectedComparison.value?.topics || []);

  const drawerSelectedTopic = ref({});

  const topicsPostVolumeTimeSeriesPerChannel = ref({});
  const filteredTopicIds = ref([]);

  const comparisonFilters = ref({});
  const temporaryFilters = ref({});

  const useEstimates = { useEstimates: true };
  const scale = computed(() => socialListeningStore.calculateScale(comparisonFilters.value));
  async function fetchSelectedComparison(comparisonId) {
    try {
      const res = await socialListeningApi.getComparison(organizationId.value, comparisonId);
      selectedComparison.value = res.data.data;
      selectedComparison.value.topics.sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      selectedComparison.value = null;
    }
  }

  const filteredTopics = computed(() => {
    let topics = selectedComparison.value?.topics ?? [];
    if (!isEmpty(filteredTopicIds.value)) {
      topics = topics.filter((topic) => filteredTopicIds.value.includes(topic.id));
    }
    return topics;
  });

  const postsPerTopicPerChannel = ref({});
  async function fetchTopicsPostVolumeByChannel() {
    const graphOptions = POST_VOLUME_GRAPH_OPTIONS.TOTAL_POSTS;
    pendingDataPull.value.topicsPostVolumePerChannel = true;

    postsPerTopicPerChannel.value = {};
    topicsPostVolumeTimeSeriesPerChannel.value = {};

    const signal = refreshAbortController(abortControllers.postsPerTopicPerChannel);

    const promises = filteredTopics.value.map(async (topic) => {
      const payload = {
        breakdownBy: graphOptions.primaryBreakdown,
        scale: scale.value,
        topicId: topic.id,
        searchFilters: {
          keywordsAndHashtags: topic.searchBody.filters.keywordsAndHashtags,
          excludedHandles: topic.searchBody.filters.excludedHandles ?? [],
          ...comparisonFilters.value,
        },
      };
      const sources =
        comparisonFilters.value.sources ??
        Object.values(SOCIAL_LISTENING_CHANNEL_OPTIONS).map((channel) => channel.value);

      try {
        const response = await getTimeSeriesPostVolume(payload, signal, useEstimates);
        topicsPostVolumeTimeSeriesPerChannel.value[topic.name] = response;

        const groupTotals = Object.fromEntries(
          sources.map((source) => {
            const sourceName = source === 'TWITTER' ? 'X' : source;
            return [sourceName, 0];
          }),
        );

        Object.values(response).forEach((data) => {
          const breakdown = data.breakdown.data;
          breakdown.forEach(({ group, count }) => {
            const sourceName = group === 'TWITTER' ? 'X' : group;
            groupTotals[sourceName] = (groupTotals[sourceName] || 0) + count;
          });
        });

        postsPerTopicPerChannel.value[topic.name] = groupTotals;
        postsPerTopicPerChannel.value = Object.fromEntries(
          Object.entries(postsPerTopicPerChannel.value).sort(([a], [b]) => a.localeCompare(b)),
        );
      } catch {
        postsPerTopicPerChannel.value[topic.name] = Object.fromEntries(
          sources.map((source) => [source, 0]),
        );
        postsPerTopicPerChannel.value = Object.fromEntries(
          Object.entries(postsPerTopicPerChannel.value).sort(([a], [b]) => a.localeCompare(b)),
        );
        topicsPostVolumeTimeSeriesPerChannel.value[topic.name] = {};
      }
    });
    await Promise.all(promises);
    pendingDataPull.value.topicsPostVolumePerChannel = false;
  }

  const topicsPercentagePostVolumePerGender = ref({});
  const genderLabels = ref(['male', 'female', 'other']);
  async function fetchTopicsPostVolumePerGender() {
    pendingDataPull.value.topicsPostVolumePerGender = true;
    topicsPercentagePostVolumePerGender.value = {};

    const signal = refreshAbortController(abortControllers.topicsPostVolumePerGender);
    const promises = filteredTopics.value.map(async (topic) => {
      const genderDataBreakdown = Object.fromEntries(
        genderLabels.value.map((gender) => [gender, 0]),
      );
      const payload = {
        breakdownBy: 'GENDER',
        secondaryBreakdownBy: 'PLATFORM',
        topicId: topic.id,
        searchFilters: {
          keywordsAndHashtags: topic.searchBody.filters.keywordsAndHashtags,
          excludedHandles: topic.searchBody.filters.excludedHandles ?? [],
          ...comparisonFilters.value,
        },
      };

      try {
        const response = await socialListeningApi.getPostVolume(
          { organizationId: organizationId.value, payload, useEstimates },
          { signal },
        );

        const breakdown = response.data.data.breakdown.data;
        const total = response.data.data.count;

        breakdown.forEach(({ group, count }) => {
          genderDataBreakdown[group] = count;
        });

        if (total > 0) {
          Object.keys(genderDataBreakdown).forEach((gender) => {
            genderDataBreakdown[gender] = Math.round((genderDataBreakdown[gender] / total) * 100);
          });
        }
        topicsPercentagePostVolumePerGender.value[topic.name] = genderDataBreakdown;
      } catch {
        topicsPercentagePostVolumePerGender.value[topic.name] = genderDataBreakdown;
      }
    });

    await Promise.all(promises);
    topicsPercentagePostVolumePerGender.value = Object.fromEntries(
      Object.entries(topicsPercentagePostVolumePerGender.value).sort(([a], [b]) =>
        a.localeCompare(b),
      ),
    );
    pendingDataPull.value.topicsPostVolumePerGender = false;
  }

  const topicsPercentagePostVolumePerAge = ref({});
  const isAgeBreakdownOnly = ref(true);
  const ageLabels = ref(['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']);
  async function fetchTopicsPostVolumePerAge() {
    pendingDataPull.value.topicsPostVolumePerAge = true;
    topicsPercentagePostVolumePerAge.value = {};

    const signal = refreshAbortController(abortControllers.topicsPostVolumePerAge);

    async function _fetchAllTopicsWithAgeAndGenderBreakdown() {
      const results = {};
      let anyFailedOrEmpty = false;

      const promises = filteredTopics.value.map(async (topic) => {
        const payload = {
          breakdownBy: 'AGE',
          secondaryBreakdownBy: 'GENDER',
          topicId: topic.id,
          searchFilters: {
            keywordsAndHashtags: topic.searchBody.filters.keywordsAndHashtags,
            excludedHandles: topic.searchBody.filters.excludedHandles ?? [],
            ...comparisonFilters.value,
          },
        };

        try {
          const response = await socialListeningApi.getPostVolume(
            { organizationId: organizationId.value, payload, useEstimates },
            { signal },
          );

          const total = response.data.data.count;
          if (total === 0) {
            anyFailedOrEmpty = true;
            return;
          }

          const breakdownData = response.data.data.breakdown.data;
          const aggregated = {};

          breakdownData.forEach(({ group: ageGroup, count: ageCount, secondaryBreakdown }) => {
            aggregated[ageGroup] = {
              all: Math.round((ageCount / total) * 100),
              male: 0,
              female: 0,
              other: 0,
            };
            secondaryBreakdown.data.forEach(({ group, count }) => {
              aggregated[ageGroup][group] = Math.round((count / total) * 100);
            });
          });

          ageLabels.value.forEach((label) => {
            if (!aggregated[label]) aggregated[label] = { all: 0, male: 0, female: 0, other: 0 };
          });

          results[topic.name] = aggregated;
        } catch {
          anyFailedOrEmpty = true;
        }
      });

      await Promise.all(promises);
      return { results, anyFailedOrEmpty };
    }

    async function _fetchTopicsWithAgeBreakdown() {
      const results = {};

      const promises = filteredTopics.value.map(async (topic) => {
        const payload = {
          breakdownBy: 'AGE',
          topicId: topic.id,
          searchFilters: {
            keywordsAndHashtags: topic.searchBody.filters.keywordsAndHashtags,
            excludedHandles: topic.searchBody.filters.excludedHandles ?? [],
            ...comparisonFilters.value,
          },
        };

        try {
          const response = await socialListeningApi.getPostVolume(
            { organizationId: organizationId.value, payload, useEstimates },
            { signal },
          );

          const breakdown = response.data.data.breakdown.data;
          const total = response.data.data.count;
          const aggregated = {};

          ageLabels.value.forEach((label) => {
            const entry = breakdown.find(({ group }) => group === label);
            const ageCount = entry ? entry.count : 0;
            aggregated[label] = {
              all: total ? Math.round((ageCount / total) * 100) : 0,
              male: 0,
              female: 0,
              other: 0,
            };
          });

          results[topic.name] = aggregated;
        } catch {
          results[topic.name] = Object.fromEntries(
            ageLabels.value.map((label) => [label, { all: 0, male: 0, female: 0, other: 0 }]),
          );
        }
      });

      await Promise.all(promises);
      return { results };
    }

    const ageAndGenderBreakdownData = await _fetchAllTopicsWithAgeAndGenderBreakdown();
    if (ageAndGenderBreakdownData.anyFailedOrEmpty) {
      const ageBreakdownData = await _fetchTopicsWithAgeBreakdown();
      topicsPercentagePostVolumePerAge.value = ageBreakdownData.results;
      isAgeBreakdownOnly.value = true;
    } else {
      topicsPercentagePostVolumePerAge.value = ageAndGenderBreakdownData.results;
      isAgeBreakdownOnly.value = false;
    }

    topicsPercentagePostVolumePerAge.value = Object.fromEntries(
      Object.entries(topicsPercentagePostVolumePerAge.value).sort(([a], [b]) => a.localeCompare(b)),
    );
    pendingDataPull.value.topicsPostVolumePerAge = false;
  }

  async function deleteComparison(comparisonId) {
    let result;
    try {
      result = await socialListeningApi.deleteComparison(organizationId.value, comparisonId);
      comparisonList.value = comparisonList.value.filter(
        (comparison) => comparison.id !== comparisonId,
      );
      if (selectedComparison.value.id === comparisonId) {
        selectedComparison.value = null;
        comparisonFilters.value = {};
      }
    } catch {
      return result;
    }
    return result;
  }

  const topicsStats = ref({});

  async function fetchTopicsStats() {
    pendingDataPull.value.topicsStats = true;

    const signal = refreshAbortController(abortControllers.topicsStats);
    const topicStatsMap = {};
    const promises = filteredTopics.value.map(async (topic) => {
      const payload = {
        topicId: topic.id,
        searchFilters: {
          keywordsAndHashtags: topic.searchBody.filters.keywordsAndHashtags,
          excludedHandles: topic.searchBody.filters.excludedHandles ?? [],
          ...comparisonFilters.value,
        },
        useEstimates: true,
        scale: scale.value,
      };
      try {
        const response = await socialListeningApi.getTopicStats(
          { organizationId: organizationId.value, ...payload },
          { signal },
        );
        topicStatsMap[topic.name] = response.data.data;
      } catch {
        topicStatsMap[topic.name] = {};
      }
    });
    await Promise.all(promises);
    topicsStats.value = topicStatsMap;
    pendingDataPull.value.topicsStats = false;
  }

  function setComparisonFilters({ topicIds, ...filtersObj }) {
    comparisonFilters.value = filtersObj;
    filteredTopicIds.value = topicIds;
  }

  function clearComparisonData() {
    pendingDataPull.value.topicsPostVolumePerChannel = true;
    pendingDataPull.value.topicsPostVolumePerGender = true;
    pendingDataPull.value.topicsPostVolumePerAge = true;
    pendingDataPull.value.topicsStats = true;

    selectedComparison.value = null;
    postsPerTopicPerChannel.value = {};
    topicsPostVolumeTimeSeriesPerChannel.value = {};
    topicsPercentagePostVolumePerGender.value = {};
    topicsPercentagePostVolumePerAge.value = {};
    topicsStats.value = {};
  }

  async function initLoadComparisonData(comparisonId) {
    clearComparisonData();
    await fetchSelectedComparison(comparisonId ?? selectedComparison.value.id);
    await Promise.all([
      fetchTopicsStats(),
      fetchTopicsPostVolumeByChannel(),
      fetchTopicsPostVolumePerGender(),
      fetchTopicsPostVolumePerAge(),
    ]);
  }

  function setTempFilters(filtersObj) {
    temporaryFilters.value = { ...filtersObj };
  }

  function clearTempFilters() {
    temporaryFilters.value = {};
  }

  function setDrawerSelectedTopic(topic) {
    drawerSelectedTopic.value = topic;
  }

  const searchFilters = computed(() => {
    const combined = omitBy(
      mapValues({ ...(comparisonFilters.value || {}), ...(temporaryFilters.value || {}) }, (v) => {
        // Convert arrays of numeric strings to numbers
        if (Array.isArray(v)) {
          return v.map((item) =>
            typeof item === 'string' && !Number.isNaN(item) && !Number.isNaN(parseFloat(item))
              ? Number(item)
              : item,
          );
        }
        return v;
      }),
      (v) => (!isNumber(v) && !isBoolean(v) && isEmpty(v)) || isNil(v),
    );
    if (combined?.sourceCreated) {
      const offset = combined?.sourceCreated?.rollingDateRangeOffset;
      let sourceCreated = combined?.sourceCreated;
      if (offset) {
        // Get date range by offset without updating topic filters
        const { start, end } = getDateRangeByOffset(offset);
        sourceCreated = {
          onOrAfter: start,
          onOrBefore: end,
          rollingDateRangeOffset: offset,
        };
      }
      return { ...combined, sourceCreated };
    }
    return combined;
  });

  const drawerSerializedFilters = computed(() => {
    const { sourceCreated, locations, ...filterData } = searchFilters.value;
    const scopedFilters = convertToScopedTopicFilters(filterData);
    const drawerFilters = mapValues(
      { ...scopedFilters, sourceCreated: null, ...sourceCreated },
      (v) => (Array.isArray(v) ? v?.join(',') : v),
    );
    drawerFilters.locations = locations; // locations are always Array[Object] as filter
    return omitBy(drawerFilters, (v) => (isEmpty(v) && !isNumber(v)) || isNil(v));
  });

  return {
    pendingDataPull,
    comparisonList,
    comparisonsUsage,
    hasExceededComparisonsLimit,
    draftMultiTopicComparison,
    draftMultiTopicComparisonIsValid,
    addTopicToDraftComparison,
    removeTopicFromDraftComparison,
    clearDraftComparison,
    updateTopicKeywordGroups,
    fetchComparisonList,
    selectedComparison,
    selectedComparisonTopics,
    fetchSelectedComparison,
    comparisonFilters,
    postsPerTopicPerChannel,
    fetchTopicsPostVolumeByChannel,
    topicsPostVolumeTimeSeriesPerChannel,
    scale,
    topicsPercentagePostVolumePerGender,
    fetchTopicsPostVolumePerGender,
    topicsPercentagePostVolumePerAge,
    isAgeBreakdownOnly,
    fetchTopicsPostVolumePerAge,
    ageLabels,
    genderLabels,
    deleteComparison,
    fetchTopicsStats,
    topicsStats,
    clearComparisonData,
    setComparisonFilters,
    filteredTopics,
    initLoadComparisonData,
    drawerSerializedFilters,
    setTempFilters,
    clearTempFilters,
    temporaryFilters,
    searchFilters,
    setDrawerSelectedTopic,
    drawerSelectedTopic,
    renameTopicFromDraftComparison,
  };
});
